import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { ADMIN_SCHEDULED_TASKS_QUERY } from '../../graphql/queries';
import { ADMIN_RUN_SCHEDULED_TASK_MUTATION, ADMIN_UPDATE_SCHEDULED_TASK_MUTATION } from '../../graphql/mutations';
import {
  IntervalUnit, describeEvery, formatDuration, groupTasks, relativeTime, splitInterval, toSeconds,
} from '../../utils/scheduledTasks';
import styles from './AdminScheduledTasks.module.css';

interface ScheduledTask {
  key: string;
  name: string;
  group: string;
  description: string;
  enabled: boolean;
  intervalSeconds: number;
  defaultIntervalSeconds: number;
  minIntervalSeconds: number;
  unavailableReason: string | null;
  running: boolean;
  pending: string | null;
  nextRunAt: string | null;
  lastStartedAt: string | null;
  lastFinishedAt: string | null;
  lastDurationMs: number | null;
  lastStatus: 'ok' | 'error' | null;
  lastMessage: string | null;
  lastError: string | null;
  lastTrigger: 'schedule' | 'manual' | null;
  runCount: number;
  failCount: number;
}

const UNITS: IntervalUnit[] = ['seconds', 'minutes', 'hours', 'days'];

const fullDate = (iso: string) => new Date(iso).toLocaleString('en-US', {
  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
});

function statusOf(t: ScheduledTask): { label: string; className: string } {
  if (t.running) return { label: 'Running', className: styles.chipRunning };
  if (t.unavailableReason) return { label: 'Cannot run here', className: styles.chipUnavailable };
  if (!t.enabled) return { label: 'Paused', className: styles.chipPaused };
  return { label: 'Active', className: styles.chipActive };
}

const TaskCard = ({ task, now }: { task: ScheduledTask; now: number }) => {
  const initial = splitInterval(task.intervalSeconds);
  const [value, setValue] = useState(String(initial.value));
  const [unit, setUnit] = useState<IntervalUnit>(initial.unit);
  const [error, setError] = useState('');

  useEffect(() => {
    const next = splitInterval(task.intervalSeconds);
    setValue(String(next.value));
    setUnit(next.unit);
  }, [task.intervalSeconds]);

  const [update, { loading: saving }] = useMutation(ADMIN_UPDATE_SCHEDULED_TASK_MUTATION, {
    onError: (e) => setError(e.message),
    refetchQueries: [{ query: ADMIN_SCHEDULED_TASKS_QUERY }],
  });
  const [run, { loading: running }] = useMutation(ADMIN_RUN_SCHEDULED_TASK_MUTATION, {
    onError: (e) => setError(e.message),
    refetchQueries: [{ query: ADMIN_SCHEDULED_TASKS_QUERY }],
  });

  const seconds = toSeconds(Number(value), unit);
  const changed = Number.isFinite(seconds) && seconds !== task.intervalSeconds;

  const save = (enabled: boolean, intervalSeconds: number) => {
    setError('');
    update({ variables: { key: task.key, enabled, intervalSeconds } });
  };

  const saveInterval = () => {
    if (!Number.isFinite(seconds) || seconds <= 0 || !Number.isInteger(seconds)) {
      setError('Enter a whole number.');
      return;
    }
    if (seconds < task.minIntervalSeconds) {
      setError(`This task cannot run more often than ${describeEvery(task.minIntervalSeconds)}.`);
      return;
    }
    save(task.enabled, seconds);
  };

  const status = statusOf(task);

  return (
    <article className={styles.card} aria-label={task.name}>
      <header className={styles.cardHeader}>
        <div className="min-w-0">
          <h4 className={styles.taskName}>{task.name}</h4>
          <code className={styles.taskKey}>{task.key}</code>
        </div>
        <span className={`${styles.chip} ${status.className}`}>{status.label}</span>
      </header>

      <p className={styles.description}>{task.description}</p>
      {task.unavailableReason && <p className={styles.unavailable}>{task.unavailableReason}</p>}

      <dl className={styles.facts}>
        <div>
          <dt>Runs</dt>
          <dd>{describeEvery(task.intervalSeconds)}</dd>
        </div>
        <div>
          <dt>Next run</dt>
          <dd title={task.nextRunAt ? fullDate(task.nextRunAt) : undefined}>
            {!task.enabled ? 'Paused' : task.nextRunAt ? relativeTime(task.nextRunAt, now) : '—'}
          </dd>
        </div>
        <div>
          <dt>Last run</dt>
          <dd title={task.lastFinishedAt ? fullDate(task.lastFinishedAt) : undefined}>
            {task.lastFinishedAt ? (
              <>
                <span className={task.lastStatus === 'error' ? styles.failed : styles.ok}>
                  {task.lastStatus === 'error' ? 'Failed' : 'OK'}
                </span>{' '}
                {relativeTime(task.lastFinishedAt, now)}
                {task.lastDurationMs != null && ` · ${formatDuration(task.lastDurationMs)}`}
                {task.lastTrigger === 'manual' && ' · by hand'}
              </>
            ) : 'Never'}
          </dd>
        </div>
        <div>
          <dt>Runs / failures</dt>
          <dd>{task.runCount} / {task.failCount}</dd>
        </div>
      </dl>

      {task.pending && <p className={styles.pending}><span className="material-symbols-outlined text-sm">hourglass_empty</span>{task.pending}</p>}
      {task.lastStatus === 'error' && task.lastError && <p className={styles.lastError} role="alert">{task.lastError}</p>}
      {task.lastStatus === 'ok' && task.lastMessage && <p className={styles.lastMessage}>{task.lastMessage}</p>}

      <div className={styles.controls}>
        <label className={styles.intervalLabel}>
          Every
          <input
            type="number"
            min={1}
            step={1}
            className={styles.intervalInput}
            value={value}
            onChange={(e) => { setError(''); setValue(e.target.value); }}
            aria-label={`${task.name} interval`}
          />
          <select
            className={styles.unitSelect}
            value={unit}
            onChange={(e) => { setError(''); setUnit(e.target.value as IntervalUnit); }}
            aria-label={`${task.name} interval unit`}
          >
            {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
        </label>
        <button className={styles.secondaryBtn} onClick={saveInterval} disabled={!changed || saving}>Save interval</button>
        {task.intervalSeconds !== task.defaultIntervalSeconds && (
          <button className={styles.linkBtn} onClick={() => save(task.enabled, task.defaultIntervalSeconds)} disabled={saving}>
            Default ({describeEvery(task.defaultIntervalSeconds)})
          </button>
        )}
        <span className={styles.spacer} />
        <button className={styles.secondaryBtn} onClick={() => save(!task.enabled, task.intervalSeconds)} disabled={saving}>
          <span className="material-symbols-outlined text-base">{task.enabled ? 'pause' : 'play_arrow'}</span>
          {task.enabled ? 'Pause' : 'Resume'}
        </button>
        <button
          className={styles.primaryBtn}
          onClick={() => { setError(''); run({ variables: { key: task.key } }); }}
          disabled={running || task.running || !!task.unavailableReason}
        >
          <span className={`material-symbols-outlined text-base ${running ? 'animate-spin' : ''}`}>{running ? 'refresh' : 'bolt'}</span>
          {running ? 'Running…' : 'Run now'}
        </button>
      </div>
      {error && <p className={styles.error} role="alert">{error}</p>}
    </article>
  );
};

const AdminScheduledTasks = () => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(t);
  }, []);

  const { data, loading, error } = useQuery(ADMIN_SCHEDULED_TASKS_QUERY, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 10_000,
  });
  const tasks: ScheduledTask[] = data?.adminScheduledTasks ?? [];
  const paused = tasks.filter((t) => !t.enabled).length;
  const failing = tasks.filter((t) => t.lastStatus === 'error').length;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className={styles.header}>
          <div>
            <h2 className="text-3xl font-bold tracking-tight text-on-surface font-headline">Scheduled Tasks</h2>
            <p className="text-on-surface-variant mt-1">
              Everything the platform runs on its own. Change how often a task runs, pause it, or run it now —
              changes apply within seconds, without a restart.
            </p>
          </div>
          {tasks.length > 0 && (
            <div className={styles.summary}>
              <span>{tasks.length} tasks</span>
              {paused > 0 && <span className={styles.summaryPaused}>{paused} paused</span>}
              {failing > 0 && <span className={styles.summaryFailing}>{failing} failing</span>}
            </div>
          )}
        </div>

        {loading && tasks.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-on-surface-variant">
            <span className="material-symbols-outlined animate-spin mr-2">refresh</span>
            Loading tasks...
          </div>
        ) : error && tasks.length === 0 ? (
          <p className={styles.loadError} role="alert">Could not load the scheduled tasks: {error.message}</p>
        ) : (
          groupTasks(tasks).map(([group, list]) => (
            <section key={group} className="space-y-3" aria-label={group}>
              <h3 className={styles.groupTitle}>{group}</h3>
              <div className={styles.grid}>
                {list.map((t) => <TaskCard key={t.key} task={t} now={now} />)}
              </div>
            </section>
          ))
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminScheduledTasks;
